if (!customElements.get('twa-home-media')) {
  class TwaHomeMedia extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.mobileVideoQuery = window.matchMedia('(max-width: 700px)');
      this.mobileVideoQuery.addEventListener('change', () => this.stopInlineVideo(), {
        signal: this.controller.signal,
      });
      // Dialog focus restoration can show :focus-visible even after a tap.
      // Keep focus in place, but only show its outline once a keyboard is used.
      this.addEventListener(
        'pointerdown',
        (event) => {
          event.target.closest('[data-zoom]')?.classList.add('twa-image-pointer-focus');
        },
        { signal: this.controller.signal },
      );
      document.addEventListener(
        'keydown',
        () => {
          this.querySelectorAll('.twa-image-pointer-focus').forEach((button) => {
            button.classList.remove('twa-image-pointer-focus');
          });
        },
        { signal: this.controller.signal },
      );
      this.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest('[data-youtube], [data-vimeo], [data-audio], [data-zoom]');
          if (!button) return;
          if (button.hasAttribute('data-audio')) return this.playAudio(button);
          this.openMedia(button);
        },
        { signal: this.controller.signal },
      );
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.stopAudio();
      this.stopInlineVideo();
      this.dialog?.close();
      this.dialog?.remove();
    }

    stopInlineVideo() {
      if (!this.inlineVideo) return;
      this.inlineVideo.replaceWith(this.inlineVideoButton);
      this.inlineVideo = null;
      this.inlineVideoButton = null;
    }

    playInlineYouTube(button) {
      const player = document.createElement('div');
      player.className = `${button.className} twa-youtube-inline`;
      const frame = document.createElement('iframe');
      frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(button.dataset.youtube)}?autoplay=1&rel=0&playsinline=1`;
      frame.title = button.getAttribute('aria-label') || 'YouTube video';
      frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      player.append(frame);
      this.inlineVideoButton = button;
      this.inlineVideo = player;
      button.replaceWith(player);
      frame.focus({ preventScroll: true });
    }

    stopAudio() {
      this.audio?.pause();
      this.audio = null;
      this.activeButton?.setAttribute('aria-pressed', 'false');
      this.activeButton = null;
      const status = this.querySelector('[role="status"]');
      if (status) status.textContent = '';
    }

    async playAudio(button) {
      if (!button.dataset.audio) {
        const status = this.querySelector('[role="status"]');
        if (status) {
          status.classList.remove('twa-sr-only');
          status.textContent = 'This audio sample is not available yet.';
        }
        return;
      }
      const wasPlaying = this.activeButton === button;
      document.querySelectorAll('twa-home-media').forEach((media) => {
        media.stopAudio();
        media.stopInlineVideo();
      });
      if (wasPlaying) return;
      const audio = new Audio(button.dataset.audio);
      this.audio = audio;
      this.activeButton = button;
      button.setAttribute('aria-pressed', 'true');
      audio.onended = () => this.stopAudio();
      const status = this.querySelector('[role="status"]');
      if (status) {
        status.classList.remove('twa-sr-only');
        status.textContent = 'Loading sample…';
      }
      const showError = () => {
        if (this.audio !== audio) return;
        this.stopAudio();
        if (status) status.textContent = 'This sample could not play. Please tap to try again.';
      };
      audio.onerror = showError;
      try {
        await audio.play();
        if (this.audio !== audio) return;
        if (status) status.textContent = `Playing ${button.textContent.trim()}. Tap again to stop.`;
      } catch {
        showError();
      }
    }

    openMedia(button) {
      let vimeoUrl;
      if (button.dataset.vimeo) {
        try {
          vimeoUrl = new URL(button.dataset.vimeo);
          if (vimeoUrl.protocol !== 'https:' || vimeoUrl.hostname !== 'player.vimeo.com') return;
          vimeoUrl.searchParams.set('autoplay', '1');
          vimeoUrl.searchParams.set('playsinline', '1');
        } catch {
          return;
        }
      }
      document.querySelectorAll('twa-home-media').forEach((media) => {
        media.stopAudio();
        media.stopInlineVideo();
      });
      if (button.dataset.youtube && !vimeoUrl && this.mobileVideoQuery.matches) {
        this.playInlineYouTube(button);
        return;
      }
      const dialog = document.createElement('dialog');
      dialog.className =
        'twa-media-dialog' + (button.hasAttribute('data-portrait') ? ' twa-media-dialog--portrait' : '');
      if (button.hasAttribute('data-app-screenshot')) dialog.classList.add('twa-media-dialog--app');
      dialog.setAttribute('aria-label', button.getAttribute('aria-label') || 'Media preview');
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'twa-media-dialog__close';
      close.setAttribute('aria-label', 'Close preview');
      close.textContent = '×';
      close.addEventListener('click', () => dialog.close());
      dialog.append(close);
      if (button.dataset.youtube || vimeoUrl) {
        const frame = document.createElement('iframe');
        frame.src = vimeoUrl
          ? vimeoUrl.href
          : `https://www.youtube-nocookie.com/embed/${encodeURIComponent(button.dataset.youtube)}?autoplay=1&rel=0&playsinline=1`;
        frame.title = button.getAttribute('aria-label');
        frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        dialog.append(frame);
      } else {
        const img = button.querySelector('img').cloneNode();
        img.removeAttribute('loading');
        dialog.append(img);
        if (button.dataset.caption || button.dataset.description) {
          dialog.classList.add('twa-media-dialog--captioned');
          const copy = document.createElement('div');
          copy.className = 'twa-media-dialog__copy';
          if (button.dataset.caption) {
            const heading = document.createElement('h2');
            heading.textContent = button.dataset.caption;
            copy.append(heading);
          }
          if (button.dataset.description) {
            const description = document.createElement('p');
            description.textContent = button.dataset.description;
            copy.append(description);
          }
          dialog.append(copy);
        }
      }
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener(
        'close',
        () => {
          dialog.remove();
          button.focus({ preventScroll: true });
          this.dialog = null;
        },
        { once: true },
      );
      document.body.append(dialog);
      this.dialog = dialog;
      dialog.showModal();
    }
  }
  customElements.define('twa-home-media', TwaHomeMedia);
}
